/* v2.1.0 — Volume everywhere + custom cut cart sync */
/**
 * Dynamic Pricing Engine — Product Page JS
 *
 * Handles variant selection, price calculation, and cart submission
 * for the 96-variant length-based pricing model.
 *
 * Each product has 96 variants (1 inch through 96 inch).
 * The variant price = per-inch price (total / length).
 * Cart quantity = number of inches ordered.
 * Customer sees TOTAL price, not per-inch.
 */

if (!customElements.get('dynamic-pricing')) {
  class DynamicPricing extends HTMLElement {
    constructor() {
      super();
      this.defaultLength = 12;
      this.minLength = 1;
      this.maxLength = 96;
      this.currentLength = this.defaultLength;
      this.currentQty = 1;
      this.cartQtyForVariant = 0; // qty already in cart for current variant
      this.isCustom = false;
      this.oversizeInfo = null; // set when entered length exceeds maxLength → routes to RFQ
      this.snapInfo = null; // set when a typed length was rounded to the nearest 1/8" → shows the snap note
      this.minInfo = null; // set when a typed length sat below a >1" product minimum → clamped up, note shown
      this.requestedLength = null; // the length the customer actually asked for, BEFORE any in-stock snap — carried into the OOS quote CTA
      this.variants = [];
      this.productUrl = '';
      this.pricingConfig = null; // sale + volume config from metafield
      // P2 i18n: strings the JS renders at runtime, injected from Liquid (locales/*.json).
      // EN fallbacks keep the UI intact if the dict is missing. t() also interpolates tokens.
      this.i18n = (typeof window !== 'undefined' && window.MRU_DYNAMIC_PRICING_I18N) || {};
      this.sanitizeI18n(this.i18n);
      this.isFr = (typeof document !== 'undefined' && document.documentElement && /^fr/i.test(document.documentElement.lang || ''));
    }

    // A translation tool can register strings HTML-escaped (&quot; -> ") or leave
    // a key missing (Shopify injects "Translation missing: ..."). Normalize the
    // injected dict once so t()'s EN fallback wins on missing keys and quotes +
    // inline tags render instead of showing as literal &quot; / &lt;span&gt; text.
    sanitizeI18n(dict) {
      if (!dict || typeof dict !== 'object') return;
      for (var k in dict) {
        if (!Object.prototype.hasOwnProperty.call(dict, k)) continue;
        var v = dict[k];
        if (typeof v !== 'string') continue;
        if (v.indexOf('Translation missing') === 0 || v.indexOf('translation missing') === 0) { delete dict[k]; continue; }
        dict[k] = this.decodeEntities(v);
      }
    }

    decodeEntities(s) {
      if (typeof s !== 'string' || s.indexOf('&') === -1) return s;
      return s.replace(/&lt;/g, '<').replace(/&gt;/g, '>')
              .replace(/&quot;/g, '"').replace(/&#0?34;/g, '"')
              .replace(/&#0?39;/g, "'").replace(/&#x27;/gi, "'")
              .replace(/&amp;/g, '&');
    }

    // Look up a P2 string by key with an EN fallback, then fill {token} placeholders.
    t(key, fallback, vars) {
      var s = (this.i18n && this.i18n[key] != null) ? this.i18n[key] : fallback;
      if (vars) {
        for (var k in vars) {
          if (Object.prototype.hasOwnProperty.call(vars, k)) {
            s = s.split('{' + k + '}').join(String(vars[k]));
          }
        }
      }
      return s;
    }

    // Snap a measurement to the nearest 1/8 inch — the precision we actually cut to.
    snapToEighth(val) { return Math.round(val * 8) / 8; }

    // Format an inch value for display: drop trailing zeros (5 / 5.5 / 5.125, never 5.000).
    fmtLen(val) { return val.toFixed(3).replace(/\.?0+$/, ''); }

    connectedCallback() {
      // Parse variant data from the embedded JSON
      const dataEl = this.querySelector('[data-dp-variants]');
      if (dataEl) {
        try {
          this.variants = JSON.parse(dataEl.textContent);
        } catch (e) {
          console.error('Dynamic Pricing: Could not parse variant data', e);
          return;
        }
      }

      // Parse pricing config (sale + volume data from metafield)
      const configEl = this.querySelector('[data-dp-pricing-config]');
      if (configEl) {
        try {
          this.pricingConfig = JSON.parse(configEl.textContent);
        } catch (e) {
          console.error('Dynamic Pricing: Could not parse pricing config', e);
        }
      }

      // Offcuts + stock state (2026-09-05). custom.offcuts is rendered only while
      // the shop switch is on; the stock attributes always come through.
      this.offcuts = [];
      const offEl = this.querySelector('[data-dp-offcuts]');
      if (offEl) {
        try {
          var offData = JSON.parse(offEl.textContent);
          this.offcuts = (offData && Array.isArray(offData.pieces)) ? offData.pieces.filter(function (p) { return p && p.len > 0 && p.n > 0 && p.price > 0; }) : [];
        } catch (e) { this.offcuts = []; }
      }
      this.offcutMode = false;
      this.stockState = this.dataset.stockState || '';
      this.stockLongest = parseFloat(this.dataset.stockLongest) || 0;
      this.stockTotal = parseFloat(this.dataset.stockTotal) || 0;
      this.offcutsChecked = this.dataset.offcutsChecked || '';
      this.productTitle = this.dataset.productTitle || '';

      // Weekly sale banner countdown (Liquid renders the banner when
      // pricing_config.saleAll is set; this just runs the clock).
      this.initWeeklyBanner();

      // Read product URL for cart
      this.productUrl = this.dataset.productUrl || '';

      // Read max length from metafield (if available)
      const metaMax = parseInt(this.dataset.maxLength, 10);
      if (metaMax && metaMax > 0 && metaMax <= 100) {
        this.maxLength = metaMax;
      }

      // Per-product minimum cut (grip strut planks: 6"). Absent attribute keeps
      // the 1" floor, so every other product behaves exactly as before.
      const metaMin = parseFloat(this.dataset.minLength);
      if (metaMin && metaMin > 1 && metaMin < this.maxLength) {
        this.minLength = metaMin;
      }

      // Compute max in-stock length AND max existing-variant length.
      // gridMaxLength = largest length that actually has a variant to price;
      // the metafield max can exceed it (missing top variants / 250-cap), and a
      // length beyond the grid has no variant — must route to RFQ, not be
      // priced off a stale value (cf. sheet undercharge, order #3371).
      this.stockMaxLength = 0;
      this.gridMaxLength = 0;
      if (this.variants) {
        for (var vi = 0; vi < this.variants.length; vi++) {
          var vLen = this.optionInches(this.variants[vi]);
          var vStock = this.variants[vi].inventoryQty || 0;
          if (!isNaN(vLen)) {
            if (vLen > this.gridMaxLength) this.gridMaxLength = vLen;
            if (vStock > 0 && vLen > this.stockMaxLength) this.stockMaxLength = vLen;
          }
        }
      }
      if (!this.gridMaxLength) this.gridMaxLength = this.maxLength;

      // Bind elements
      this.heroPrice = this.querySelector('[data-dp-hero-price]');
      this.heroOrig = this.querySelector('[data-dp-hero-orig]');
      this.heroLabel = this.querySelector('[data-dp-hero-label]');
      this.heroSavings = this.querySelector('[data-dp-hero-savings]');
      this.savingsText = this.querySelector('[data-dp-savings-text]');
      this.saleText = this.querySelector('[data-dp-sale-text]');
      this.lowStockBadge = this.querySelector('[data-dp-low-stock]');
      this.stockHint = this.querySelector('[data-dp-stock-hint]');
      this.volumeCallout = this.querySelector('[data-dp-volume-callout]');
      this.totalOrig = this.querySelector('[data-dp-total-orig]');
      this.customInput = this.querySelector('[data-dp-custom-input]');
      this.customWrap = this.querySelector('[data-dp-custom-wrap]');
      this.applyBtn = this.querySelector('[data-dp-apply-btn]');
      this.cutHint = this.querySelector('[data-dp-cut-hint]');
      this.oversizeEl = this.querySelector('[data-dp-oversize]');
      this.oosQuoteEl = this.querySelector('[data-dp-oos-quote]');
      this.snapNote = this.querySelector('[data-dp-snap-note]');
      this.sliderFill = this.querySelector('[data-dp-slider-fill]');
      this.customWeightNote = this.querySelector('[data-dp-custom-weight]');
      this.customWeightVal = this.querySelector('[data-dp-custom-weight-val]');
      this.cutSummary = this.querySelector('[data-dp-cut-summary]');
      this.cutTotal = this.querySelector('[data-dp-cut-total]');
      this.addToCartBtn = this.querySelector('[data-dp-add-cart]');
      this.variantIdInput = this.querySelector('[data-dp-variant-id]');
      this.qtyInput = this.querySelector('[data-dp-qty-hidden]');
      this.lineItemProps = this.querySelector('[data-dp-line-props]');
      this.qtyField = this.querySelector('[data-dp-qty-visible]');
      this.qtyMinusBtn = this.querySelector('[data-dp-qty-minus]');
      this.qtyPlusBtn = this.querySelector('[data-dp-qty-plus]');
      this.qtyDesc = this.querySelector('[data-dp-qty-desc]');
      this.shipWeight = this.querySelector('[data-dp-ship-weight]');
      this.tabsEl = this.querySelector('[data-dp-tabs]');
      this.offcutPanel = this.querySelector('[data-dp-offcut-panel]');
      // Deep link (2026-09-11): /products/x#offcuts lands with the Offcuts tab
      // open and in view, so the "See pieces" button on /collections/offcuts
      // never drops a customer on Cut to length. No-op without pieces.
      this.deepLinkOffcuts = false;
      try { this.deepLinkOffcuts = window.location.hash === '#offcuts'; } catch (e) { this.deepLinkOffcuts = false; }
      this.finePrint = this.querySelector('.dp-config-fine-print');
      this.fineDefault = this.finePrint ? this.finePrint.innerHTML : '';

      // Default to 12" (parent variant) — always treat as custom cut
      this.isCustom = true;
      this.currentLength = this.defaultLength;

      this.setupEventListeners();
      this.render();
      if (this.deepLinkOffcuts && this.offcuts.length && this.tabsEl) {
        this.enterOffcutMode(true);
        var tabs = this.tabsEl;
        setTimeout(function () { try { tabs.scrollIntoView({ block: 'start', behavior: 'smooth' }); } catch (e) { /* older browsers */ } }, 60);
      }

      // Fetch cart to check existing qty for volume calculations
      this.fetchCartQty();

      // Debounced cart re-sync handler
      this._debouncedCartSync = null;
      this._cartSyncHandler = () => {
        clearTimeout(this._debouncedCartSync);
        this._debouncedCartSync = setTimeout(() => this.fetchCartQty(), 300);
      };
      this._visibilityHandler = () => {
        if (!document.hidden) this._cartSyncHandler();
      };

      // Re-sync cart when page regains focus (catches cart removals, other tab edits)
      document.addEventListener('visibilitychange', this._visibilityHandler);
      // Re-sync when cart drawer closes (user may have removed items)
      document.addEventListener('on:cart-drawer:after-close', this._cartSyncHandler);
      document.addEventListener('dispatch:cart-drawer:close', this._cartSyncHandler);
      // Re-sync on any cart fetch/render cycle from the theme
      document.addEventListener('on:cart:add', this._cartSyncHandler);
      document.addEventListener('dispatch:cart-drawer:refresh', this._cartSyncHandler);
      document.addEventListener('on:line-item:change', this._cartSyncHandler);
    }

    disconnectedCallback() {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      document.removeEventListener('on:cart-drawer:after-close', this._cartSyncHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this._cartSyncHandler);
      document.removeEventListener('on:cart:add', this._cartSyncHandler);
      document.removeEventListener('dispatch:cart-drawer:refresh', this._cartSyncHandler);
      document.removeEventListener('on:line-item:change', this._cartSyncHandler);
      clearTimeout(this._debouncedCartSync);
      if (this._weeklyTimer) clearInterval(this._weeklyTimer);
    }

    // Weekly sale banner: countdown to the Sunday that closes the set.
    // saleEndsAt is a plain YYYY-MM-DD; the deadline is 11:59 pm in the
    // CUSTOMER's local time (the server-side flip is the next Monday run,
    // so a time-zone hour either way can't sell at a wrong price).
    initWeeklyBanner() {
      var banner = this.querySelector('[data-dp-weekly-banner]');
      if (!banner) return;
      var cfg = this.pricingConfig || {};
      var m = cfg.saleAll && cfg.saleEndsAt ? String(cfg.saleEndsAt).match(/^(\d{4})-(\d{2})-(\d{2})$/) : null;
      if (!m) { banner.hidden = true; return; }
      var end = new Date(+m[1], +m[2] - 1, +m[3], 23, 59, 59);
      var out = banner.querySelector('[data-dp-weekly-countdown]');
      var self = this;
      var tick = function() {
        var left = end.getTime() - Date.now();
        if (left <= 0) {
          // Past the end date (customer lingering across midnight, or the
          // Monday flip run hasn't happened yet): drop the urgency claim.
          banner.hidden = true;
          if (self._weeklyTimer) clearInterval(self._weeklyTimer);
          return;
        }
        if (!out) return;
        var d = Math.floor(left / 86400000);
        var h = Math.floor((left % 86400000) / 3600000);
        var mn = Math.floor((left % 3600000) / 60000);
        out.textContent = d > 0
          ? self.t('weeklyLeft', '{d}d {h}h left', { d: d, h: h })
          : self.t('weeklyLeftHours', '{h}h {m}m left', { h: h, m: mn });
      };
      tick();
      this._weeklyTimer = setInterval(tick, 60000);
    }

    // Fetch current cart and build a map of variant_id → qty
    async fetchCartQty() {
      try {
        var res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        if (!res.ok) return;
        var cart = await res.json();
        this._cartMap = {};
        this._offcutInCart = {};
        for (var i = 0; i < (cart.items || []).length; i++) {
          var item = cart.items[i];
          this._cartMap[item.variant_id] = (this._cartMap[item.variant_id] || 0) + item.quantity;
          if (item.properties && item.properties._Offcut != null) {
            var k = String(item.properties._Offcut);
            this._offcutInCart[k] = (this._offcutInCart[k] || 0) + (item.quantity || 0);
          }
        }
        this.syncCartQtyForCurrentVariant();
        this.syncOffcutButtons();
      } catch (e) { /* silent */ }
    }

    // Sync cartQtyForVariant from the cart map for the currently selected length
    syncCartQtyForCurrentVariant() {
      // For custom cuts (fractional lengths), round up to find the actual variant in cart
      var lookupLen = this.isCustom ? Math.ceil(this.currentLength) : this.currentLength;
      var variant = this.findVariant(lookupLen);
      var variantId = variant ? parseInt(variant.id) : 0;
      var qty = (this._cartMap && this._cartMap[variantId]) || 0;
      if (this.cartQtyForVariant !== qty) {
        this.cartQtyForVariant = qty;
        this.render();
      }
    }

    // Largest cut we can actually PRICE online = the smaller of the metafield
    // max and the variant-grid max. Beyond this there's no variant → route to RFQ.
    effMaxLength() { return Math.min(this.maxLength, this.gridMaxLength || Infinity); }

    // Apply a typed cut length. Lengths above the effective max flag an over-max
    // state (message + RFQ route) instead of being silently clamped/unpriced.
    // `commit` is true on a finished change (blur / Enter): we then reflect the
    // snapped value back into the field and show the snap note. While the customer
    // is still typing (debounced input, commit=false) we snap silently for price.
    applyCustomLength(raw, commit) {
      var rawVal = parseFloat(raw);
      var maxLen = this.effMaxLength();
      // Incomplete or below the 1" floor — ignore (prior behavior), clear any flag.
      if (isNaN(rawVal) || rawVal <= 0 || (rawVal < this.minLength && this.minLength <= 1)) {
        this.snapInfo = null;
        this.minInfo = null;
        if (this.oversizeInfo) this.oversizeInfo = null;
        this.render();
        return;
      }
      // Below a real product minimum (grip strut: 6"): clamp up and say so.
      // Only products that declare data-min-length ever reach this branch.
      if (rawVal < this.minLength) {
        this.requestedLength = rawVal;
        this.isCustom = true;
        this.oversizeInfo = null;
        this.snapInfo = null;
        this.minInfo = { req: rawVal };
        this.currentLength = this.minLength;
        if (commit && this.customInput) this.customInput.value = this.fmtLen(this.minLength);
        this.snapToNearestInStock();
        this.syncCartQtyForCurrentVariant();
        this.render();
        return;
      }
      this.minInfo = null;
      // Snap the typed length to the nearest 1/8" — the precision we cut to. Pricing
      // still rounds this UP to the next whole inch (see calcTotal), unchanged.
      var val = this.snapToEighth(rawVal);
      var moved = Math.abs(val - rawVal) > 1e-6;
      // Remember what the customer asked for BEFORE the in-stock snap below,
      // so the OOS quote CTA can carry their original length.
      this.requestedLength = val;
      this.isCustom = true;
      if (val > maxLen) {
        this.oversizeInfo = { reqLen: val };
        this.currentLength = maxLen; // clamp to a length that HAS a variant
        this.snapInfo = null;        // oversize routes to RFQ; no snap note
      } else {
        this.oversizeInfo = null;
        this.currentLength = val;
        this.snapInfo = (commit && moved) ? { to: val } : null;
      }
      if (commit && moved && this.customInput) this.customInput.value = this.fmtLen(val);
      this.snapToNearestInStock();
      this.syncCartQtyForCurrentVariant();
      this.render();
    }

    // Locale-aware route prefix ('/' on EN, '/fr/' on FR) so JS-built links
    // keep the customer on their language.
    routesRoot() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }

    // Deep link into the RFQ flow (/pages/quote) pre-filled with this product +
    // the length the customer wanted. See rfq-form.js parsePrefill(). `note`
    // is optional; without it the over-max wording is used (original behavior).
    buildRfqUrl(reqLen, note) {
      var handle = this.dataset.productHandle ||
        ((this.productUrl || '').split('/products/')[1] || '').split('?')[0] || '';
      var parts = ['product=' + encodeURIComponent(handle), 'qty=' + (this.currentQty || 1)];
      if (reqLen != null && !isNaN(reqLen)) parts.push('length=' + encodeURIComponent(reqLen));
      if (note == null) {
        note = this.t('rfqNote', 'Looking for a {len}" cut — longer than the {max}" online max.', { len: (reqLen != null ? reqLen : '?'), max: this.effMaxLength() });
      }
      parts.push('note=' + encodeURIComponent(note));
      return this.routesRoot() + 'pages/quote?' + parts.join('&');
    }

    // Show / hide the over-max message + RFQ CTA, flag the input, toggle the
    // host class that dims the price + slider.
    renderOversize() {
      var el = this.oversizeEl;
      var info = this.oversizeInfo;
      if (!el) return;
      if (!info) {
        el.hidden = true;
        el.innerHTML = '';
        this.classList.remove('dp-is-oversize');
        if (this.customInput) this.customInput.classList.remove('dp-input-error');
        return;
      }
      var reqLen = info.reqLen;
      var href = this.buildRfqUrl(reqLen);
      el.innerHTML =
        '<div class="dp-oversize__title">' + this.t('oversizeTitle', 'Longer than our online max') + '</div>' +
        '<div class="dp-oversize__body">' +
          this.t('oversizeBodyPre', 'Online orders for this product go up to') + ' <strong>' + this.effMaxLength() + '"</strong>. ' +
          this.t('oversizeBodyMid', 'You entered') + ' <strong>' + reqLen + '"</strong>' +
          this.t('oversizeBodyPost', ' — request a quote and our team will see what we can do for a longer cut.') + ' ' + this.t('srcTail', 'We bring more in for orders of $250 or more, 10 to 21 days.') + '</div>' +
        '<a class="dp-oversize__cta" href="' + href + '">' + this.t('oversizeCta', 'Request a quote for {n}"', { n: reqLen }) +
          '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4"/></svg></a>' +
        // On a short shelf the online max IS the longest piece, so this block is
        // where the over-shelf request lands: offer the stock alert here too.
        ((this.stockState === 'low' || this.stockState === 'offcuts_only') ? this.alertHtml(reqLen) : '');
      el.hidden = false;
      this.classList.add('dp-is-oversize');
      if (this.customInput) this.customInput.classList.add('dp-input-error');
    }

    // Calm note shown when a typed length was rounded to the nearest 1/8". Hidden
    // while oversize (that routes to RFQ) or when the entry was already on the grid.
    renderSnapNote() {
      var el = this.snapNote;
      if (!el) return;
      // A below-minimum entry owns the note slot (it clamped the length).
      if (!this.oversizeInfo && this.minInfo) {
        el.textContent = this.t('minNote', 'The minimum cut for this product is {min}". We set your cut to {min}".', { min: this.fmtLen(this.minLength) });
        el.hidden = false;
        return;
      }
      if (this.oversizeInfo || !this.snapInfo) {
        el.hidden = true;
        el.textContent = '';
        return;
      }
      el.textContent = this.t('snapNote', 'We cut to the nearest 1/8 inch, so your cut is {len}".', { len: this.fmtLen(this.snapInfo.to) });
      el.hidden = false;
    }

    setupEventListeners() {
      // Custom input — debounced live update (no Apply button)
      this._inputDebounce = null;
      this.customInput.addEventListener('input', () => {
        clearTimeout(this._inputDebounce);
        this._inputDebounce = setTimeout(() => this.applyCustomLength(this.customInput.value, false), 300);
      });

      // Finished editing (blur or spinner) — snap the field to the nearest 1/8".
      this.customInput.addEventListener('change', () => {
        clearTimeout(this._inputDebounce);
        this.applyCustomLength(this.customInput.value, true);
      });

      // Enter key in input triggers immediate update + snap
      this.customInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          clearTimeout(this._inputDebounce);
          this.applyCustomLength(this.customInput.value, true);
        }
      });

      // Qty stepper
      if (this.qtyMinusBtn) {
        this.qtyMinusBtn.addEventListener('click', () => {
          if (this.currentQty > 1) {
            this.currentQty--;
            if (this.qtyField) this.qtyField.value = this.currentQty;
            this.render();
          }
        });
      }
      if (this.qtyPlusBtn) {
        this.qtyPlusBtn.addEventListener('click', () => {
          if (this.currentQty < 99) {
            this.currentQty++;
            if (this.qtyField) this.qtyField.value = this.currentQty;
            this.render();
          }
        });
      }
      if (this.qtyField) {
        this.qtyField.addEventListener('change', (e) => {
          this.currentQty = Math.max(1, Math.min(99, parseInt(e.target.value) || 1));
          e.target.value = this.currentQty;
          this.render();
        });
      }

      // Add to cart
      this.addToCartBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.addToCart();
      });

      // Offcuts: the tabs, the per-piece add buttons, the notice's alt actions,
      // and the stock-alert capture (2026-09-07).
      if (this.tabsEl) {
        this.tabsEl.addEventListener('click', (e) => {
          var b = e.target.closest('[data-dp-tab]');
          if (!b) return;
          this.enterOffcutMode(b.dataset.dpTab === 'offcuts');
        });
      }
      if (this.offcutPanel) {
        this.offcutPanel.addEventListener('click', (e) => {
          var add = e.target.closest('[data-dp-add]');
          if (add) this.addOffcutToCart(parseInt(add.dataset.dpAdd, 10), add);
        });
      }
      this.addEventListener('click', (e) => {
        var alt = e.target.closest('[data-dp-alt-qty]');
        if (alt) { this.currentQty = Math.max(1, parseInt(alt.dataset.dpAltQty, 10) || 1); if (this.qtyField) this.qtyField.value = this.currentQty; this.render(); }
        var see = e.target.closest('[data-dp-see-offcuts]');
        if (see) this.enterOffcutMode(true);
        var open = e.target.closest('[data-dp-alert-open]');
        if (open) { e.preventDefault(); this.openStockAlert(open); }
        var send = e.target.closest('[data-dp-alert-send]');
        if (send) { e.preventDefault(); this.submitStockAlert(send.closest('[data-dp-alert]')); }
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && e.target.matches && e.target.matches('[data-dp-alert-email]')) { e.preventDefault(); this.submitStockAlert(e.target.closest('[data-dp-alert]')); }
      });
    }

    // ── Offcuts (2026-09-05, reworked 2026-09-07) ──────────────────
    // Shelf pieces the daily update priced. Step 1 gets a second tab; every
    // piece in the list carries its own Add to cart, so a customer with ten
    // offcuts to pick from never scrolls down to Step 3 and back. The line
    // rides the FLOOR bracket variant for that length (it always has inventory
    // when the piece exists) with Your Cut = the real length, so the packer,
    // cut ticket and the overselling check read it like any cut, plus the
    // hidden _Offcut attribute the offcut-price Function reprices from the
    // product metafield. Copy never mentions cut fees.
    enterOffcutMode(on) {
      on = !!on && this.offcuts.length > 0;
      if (this.offcutMode === on) return;
      this.offcutMode = on;
      this.classList.toggle('dp-offcut-mode', on);
      if (this.tabsEl) {
        this.tabsEl.querySelectorAll('[data-dp-tab]').forEach((b) => {
          var isOn = (b.dataset.dpTab === 'offcuts') === on;
          b.classList.toggle('on', isOn);
          b.setAttribute('aria-selected', isOn ? 'true' : 'false');
        });
      }
      if (this.offcutPanel) this.offcutPanel.hidden = !on;
      if (on) this.renderOffcutPanel();
      this.render();
      if (on && this.offcutPanel) this.offcutPanel.scrollIntoView({ block: 'nearest' });
    }

    // Bracket variant an offcut rides on: the largest whole-inch variant at or
    // under the piece length (a 23.75" piece rides the 23" variant). Falls back
    // to the next whole inch, then the shortest variant that exists.
    offcutVariant(len) {
      var v = this.findVariant(Math.floor(len)) || this.findVariant(Math.ceil(len));
      if (!v && this.variants && this.variants.length) v = this.variants[0];
      return v || null;
    }

    lenLabel(len) { return this.fmtLen(len) + this.t('unitSuffix', '"'); }

    renderOffcutPanel() {
      if (!this.offcutPanel) return;
      var first = this.offcuts[0], last = this.offcuts[this.offcuts.length - 1];
      var range = this.lenLabel(first.len) + ' ' + this.t('offRangeTo', 'to') + ' ' + this.lenLabel(last.len);
      var count = this.offcuts.reduce(function (a, p) { return a + p.n; }, 0);
      var rows = this.offcuts.map((p, i) => {
        return '<div class="dp-off">' +
          '<div class="dp-off__info"><div class="dp-off__len">' + this.lenLabel(p.len) + '</div><div class="dp-off__meta">' +
          (p.n > 1 ? this.t('offAvail', '{n} available', { n: p.n }) : this.t('offOne', 'one')) + '</div></div>' +
          '<div class="dp-off__p"><b>' + this.fmt(p.price) + '</b>' + (p.cut > p.price ? '<s>' + this.fmt(p.cut) + '</s>' : '') + '</div>' +
          '<button type="button" class="dp-off__atc" data-dp-add="' + i + '">' + this.t('offRowAtc', 'Add to cart') + '</button>' +
          '</div>';
      }).join('');
      this.offcutPanel.innerHTML =
        '<div class="dp-off-lead">' + this.t('offLead', '<b>{n} pieces</b>, {range}, already cut and ready to go', { n: count, range: range }) + '</div>' +
        // Tolerance (Aaron 2026-09-11): offcuts are ±1/2", not the ±1/8" of a fresh cut; a kerf can
        // have come off the piece. Said on every offcut surface, so it is its own line here.
        '<div class="dp-off-tol">' + this.t('offTol', 'Lengths on offcuts are <b>±1/2"</b>, not our usual ±1/8". Most come in right on, but a saw kerf can take a bit off.') + '</div>' +
        '<div class="dp-off-list">' + rows + '</div>' +
        '<div class="dp-off-foot">' + this.t('offFoot', 'Sold as is. Shelf checked {date}. First order takes it.', { date: this.offcutsChecked || this.t('offToday', 'today') }) + '</div>';
      this.syncOffcutButtons();
    }

    // A row whose piece is fully in the cart stays "In your cart" (greyed) until
    // the customer takes it out again (Aaron, 2026-09-07). Driven by the cart
    // itself, so it survives reloads and clears when the drawer removes it.
    syncOffcutButtons() {
      if (!this.offcutPanel || !this._offcutInCart) return;
      this.offcuts.forEach((p, i) => {
        var btn = this.offcutPanel.querySelector('[data-dp-add="' + i + '"]');
        if (!btn || btn.dataset.busy === '1') return;
        var held = this._offcutInCart[String(p.len)] || 0;
        var out = held >= (p.n || 1);
        btn.disabled = out;
        btn.classList.toggle('is-out', out);
        btn.classList.remove('added');
        btn.textContent = out ? this.t('offRowInCart', 'In your cart') : this.t('offRowAtc', 'Add to cart');
      });
    }

    // The price band while the Offcuts tab is open: the cheapest piece, "and up".
    renderOffcutHero() {
      var min = this.offcuts.reduce(function (m, p) { return Math.min(m, p.price); }, Infinity);
      if (isFinite(min)) this.heroPrice.textContent = this.fmt(min);
      this.heroLabel.textContent = this.t('offHeroLabel', 'and up, offcuts on the shelf');
      if (this.heroOrig) this.heroOrig.classList.remove('visible');
      if (this.heroSavings) this.heroSavings.classList.remove('visible');
    }

    // ── Stock alert (2026-09-07) ─────────────────────────────────────
    // Under the $250 sourcing floor we don't bring stock in on request, but the
    // shelf changes with every delivery. The customer leaves an email and
    // Klaviyo's back-in-stock subscription on the bracket variant that would
    // cover their length (the daily update zeroes every bracket the shelf can't
    // cut, so a new mill length flips it back) sends the email. No backend of
    // ours in the path; the public key comes from the onsite script Klaviyo
    // already loads on every page.
    klaviyoCompanyId() {
      var sc = document.querySelector('script[src*="klaviyo.js"]');
      var m = sc && sc.src && sc.src.match(/company_id=([A-Za-z0-9]+)/);
      return m ? m[1] : '';
    }

    // The variant that comes back in stock when the shelf can cover this
    // length. 0 = the longest bracket on the page (a full length arrived).
    alertVariant(len) {
      var longest = null;
      (this.variants || []).forEach((v) => {
        var n = this.optionInches(v);
        if (!isNaN(n) && (!longest || n > this.optionInches(longest))) longest = v;
      });
      if (len > 0) {
        var v = this.findVariant(Math.ceil(len));
        if (v) return v;
      }
      return longest;
    }

    alertHtml(len) {
      if (!this.klaviyoCompanyId() || !this.alertVariant(len)) return '';
      return '<div class="dp-alert" data-dp-alert data-dp-alert-len="' + (len > 0 ? len : 0) + '">' +
        '<span class="dp-alert__lead">' + this.t('alertLead', 'Under $250? The shelf changes with every delivery. Leave your email and we\'ll tell you when there\'s more.') + '</span> ' +
        '<button type="button" class="dp-link-btn" data-dp-alert-open>' + this.t('alertOpen', 'Send me a stock alert') + '</button>' +
        '<div class="dp-alert__form" hidden><input type="email" data-dp-alert-email autocomplete="email" inputmode="email" placeholder="' + this.t('alertPlaceholder', 'you@example.com') + '" aria-label="' + this.t('alertPlaceholder', 'you@example.com') + '"><button type="button" class="dp-btn-dark" data-dp-alert-send>' + this.t('alertSend', 'Notify me') + '</button></div>' +
        '<div class="dp-alert__msg" data-dp-alert-msg role="status" aria-live="polite" hidden></div>' +
        '</div>';
    }

    openStockAlert(btn) {
      var box = btn.closest('[data-dp-alert]') || this.querySelector('[data-dp-alert]');
      if (!box) return;
      box.hidden = false;
      var form = box.querySelector('.dp-alert__form');
      if (form) form.hidden = false;
      var opener = box.querySelector('[data-dp-alert-open]');
      if (opener) opener.hidden = true;
      if (btn !== opener) btn.hidden = true;
      var input = box.querySelector('[data-dp-alert-email]');
      if (input) input.focus();
    }

    async submitStockAlert(box) {
      if (!box) return;
      var input = box.querySelector('[data-dp-alert-email]');
      var send = box.querySelector('[data-dp-alert-send]');
      var msg = box.querySelector('[data-dp-alert-msg]');
      var email = (input && input.value || '').trim();
      var say = (text, bad) => { if (!msg) return; msg.textContent = text; msg.hidden = false; msg.classList.toggle('is-error', !!bad); };
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { say(this.t('alertInvalid', 'Enter a valid email address.'), true); if (input) input.focus(); return; }
      var variant = this.alertVariant(parseFloat(box.dataset.dpAlertLen) || 0);
      var company = this.klaviyoCompanyId();
      if (!variant || !company) { say(this.t('alertError', 'That didn\'t go through. Call 902-468-1112 and we\'ll note it by hand.'), true); return; }
      if (send) { send.disabled = true; send.textContent = this.t('alertSending', 'Sending'); }
      try {
        var res = await fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=' + encodeURIComponent(company), {
          method: 'POST',
          headers: { 'Content-Type': 'application/vnd.api+json', 'revision': '2024-10-15' },
          body: JSON.stringify({ data: { type: 'back-in-stock-subscription',
            attributes: { channels: ['EMAIL'], profile: { data: { type: 'profile', attributes: { email: email } } } },
            relationships: { variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variant.id } } } } }),
        });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        var form = box.querySelector('.dp-alert__form');
        if (form) form.hidden = true;
        var lead = box.querySelector('.dp-alert__lead');
        if (lead) lead.hidden = true;
        say(this.t('alertDone', 'Noted. We\'ll email {email} when there\'s more on the shelf.', { email: email }), false);
      } catch (e) {
        say(this.t('alertError', 'That didn\'t go through. Call 902-468-1112 and we\'ll note it by hand.'), true);
        if (send) { send.disabled = false; send.textContent = this.t('alertSend', 'Notify me'); }
      }
    }

    // Whole inches a length variant stands for. The option value is "12\" length"
    // in EN and "longueur 12 po" in FR (translated option values, 2026-08), so
    // read the first number rather than parseInt from the start: parseInt on
    // the FR string is NaN and the whole FR configurator fell into its
    // no-price fallback (found 2026-09-07).
    optionInches(v) {
      if (!v) return NaN;
      var m = String(v.option || v.option1 || v.title || '').match(/\d+(?:\.\d+)?/);
      return m ? parseInt(m[0], 10) : NaN;
    }

    // Find the variant for a given whole-inch length
    findVariant(lengthInches) {
      const target = parseInt(lengthInches, 10);
      if (isNaN(target)) return undefined;
      return this.variants.find(v => this.optionInches(v) === target);
    }

    // Calculate the total price for a given length
    calcTotal(length) {
      const pricingLength = Math.ceil(length); // round up to nearest whole inch
      const variant = this.findVariant(pricingLength);
      if (!variant) return null;

      // variant.price IS the total price for this length (cut fee already included)
      // Strip commas in case money filter includes them (e.g. "1,119.16")
      const total = parseFloat(String(variant.price).replace(/,/g, ''));
      const perInch = total / pricingLength;

      return {
        total,
        perInch,
        pricingLength,
        variantId: variant.id,
      };
    }

    fmt(n) {
      if (this.isFr) {
        var p = n.toFixed(2).split('.');
        return p[0].replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0') + ',' + p[1] + '\u00a0$';
      }
      return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    // ── Volume discount tiers ──
    // Legacy score-based tiers (fallback when pricing_config has no tier ladder)
    static VOLUME_TIERS = [
      { minScore: 5000, minQty: 2,  discount: 0.10, label: '10%' },
      { minScore: 1000, minQty: 3,  discount: 0.08, label: '8%'  },
      { minScore: 250,  minQty: 4,  discount: 0.06, label: '6%'  },
      { minScore: 50,   minQty: 6,  discount: 0.05, label: '5%'  },
      { minScore: 0,    minQty: 10, discount: 0.05, label: '5%'  },
    ];

    // Convert variant weight (always grams from Liquid) to pounds
    weightToLb(weightGrams) {
      if (!weightGrams || weightGrams <= 0) return 0;
      return weightGrams / 453.592;
    }

    // Get the volume tier ladder for a length from pricing_config (new multi-tier system)
    // Returns { ladder, firstTier, maxQty, lowStock } or null
    getVolumeLadder(len) {
      if (!this.pricingConfig || !this.pricingConfig.volumeTiers) return null;
      const vt = this.pricingConfig.volumeTiers.find(function(v) { return v.length === len; });
      if (!vt || !vt.tiers || vt.tiers.length === 0) return null;
      const variant = this.findVariant(len);
      const stock = variant ? (variant.inventoryQty || 0) : 0;
      const firstTier = vt.tiers[0];
      // Return ladder with lowStock flag instead of null when stock can't reach first tier
      if (stock < firstTier.minQty) {
        return { ladder: vt.tiers, firstTier: firstTier, maxQty: vt.maxQty || stock, lowStock: true };
      }
      return { ladder: vt.tiers, firstTier: firstTier, maxQty: vt.maxQty || stock, lowStock: false };
    }

    // Get the per-product discount curve for custom cuts.
    // Interpolates between the two nearest sample points for the given length.
    // Returns { ladder, firstTier, maxQty, isCustomCurve } or null
    getCustomCutLadder(len) {
      if (!this.pricingConfig || !this.pricingConfig.customCutCurve) return null;
      var curve = this.pricingConfig.customCutCurve;
      if (!curve || curve.length === 0) return null;

      // Find the two nearest sample points that bracket this length
      var lower = null;
      var upper = null;
      for (var i = 0; i < curve.length; i++) {
        if (curve[i].length <= len) lower = curve[i];
        if (curve[i].length >= len && !upper) upper = curve[i];
      }

      // Exact match or only one bound
      var sample = null;
      if (lower && upper && lower.length === upper.length) {
        sample = lower; // exact match
      } else if (lower && upper) {
        // Interpolate: use the sample closer to the custom length
        // (For tiers, we use the MORE conservative / longer sample to avoid over-discounting)
        sample = upper;
      } else {
        sample = lower || upper;
      }

      if (!sample || !sample.tiers || sample.tiers.length === 0) return null;
      return { ladder: sample.tiers, firstTier: sample.tiers[0], maxQty: sample.maxQty, isCustomCurve: true };
    }

    // Find the active tier and next tier for a given qty from a ladder
    getActiveTierFromLadder(ladder, qty) {
      if (!ladder || ladder.length === 0) return { active: null, next: ladder ? ladder[0] : null };
      var active = null;
      var next = null;
      for (var i = 0; i < ladder.length; i++) {
        if (qty >= ladder[i].minQty) {
          active = ladder[i];
          next = (i + 1 < ladder.length) ? ladder[i + 1] : null;
        }
      }
      if (!active) next = ladder[0]; // below first tier, next = first tier
      return { active: active, next: next };
    }

    // Legacy: Get score-based tier for a variant (used when no pricing_config ladder)
    getScoreTier(price, weightLb) {
      if (!weightLb || weightLb <= 0) return null;
      var score = price * weightLb;
      for (var i = 0; i < DynamicPricing.VOLUME_TIERS.length; i++) {
        if (score >= DynamicPricing.VOLUME_TIERS[i].minScore) return DynamicPricing.VOLUME_TIERS[i];
      }
      return DynamicPricing.VOLUME_TIERS[DynamicPricing.VOLUME_TIERS.length - 1];
    }

    // Get volume tier info for a variant (uses ladder if available, falls back to score)
    // Returns { tier, lowStock } or null
    getVolumeTierForVariant(variant) {
      if (!variant) return null;
      // Try pricing_config ladder first
      var len = this.optionInches(variant);
      if (!isNaN(len)) {
        var ladder = this.getVolumeLadder(len);
        if (ladder) return { tier: ladder.firstTier, lowStock: ladder.lowStock };
      }
      // If product has pricing_config, DON'T fall back to legacy scores.
      // A length that isn't in the tier ladder simply has no volume discount.
      if (this.pricingConfig && this.pricingConfig.volumeTiers) return null;
      // Legacy score-based fallback (only for products without pricing_config)
      var price = parseFloat(String(variant.price).replace(/,/g, ''));
      var weightLb = this.weightToLb(variant.weightGrams);
      var tier = this.getScoreTier(price, weightLb);
      if (!tier) return null;
      var stock = variant.inventoryQty || 0;
      if (stock < tier.minQty) return null;
      return { tier: tier, lowStock: false };
    }

    // Get volume info for a specific length
    getVolumeTier(len) {
      var result = this.getVolumeTierForVariant(this.findVariant(len));
      return result ? result.tier : null;
    }

    // Classify a standard length: sale, volume, volume-low, both, oos, or default
    classifyLength(len) {
      // saleAll = weekly whole-SKU sale: every length carries the discount.
      var isSale = this.pricingConfig && (this.pricingConfig.saleAll || (this.pricingConfig.saleLengths || []).includes(len));
      var variant = this.findVariant(len);
      var stock = variant ? (variant.inventoryQty || 0) : 0;
      // Out of stock — not selectable
      if (stock <= 0) return 'oos';
      var weightLb = variant ? this.weightToLb(variant.weightGrams) : 0;
      var volResult = weightLb > 0 ? this.getVolumeTierForVariant(variant) : null;
      var isVolume = volResult !== null && !volResult.lowStock;
      var isVolumeLow = volResult !== null && volResult.lowStock;
      // In stock but no volume tiers at this length (stock too low or pieces too short)
      if (!isVolume && !isVolumeLow && this.pricingConfig && this.pricingConfig.volumeTiers && this.pricingConfig.volumeTiers.length > 0) {
        isVolumeLow = true;
      }
      if (isSale && isVolume) return 'both';
      if (isSale) return 'promo';
      if (isVolume) return 'volume';
      if (isVolumeLow) return 'volume-low';
      return 'default';
    }

    render() {
      const price = this.calcTotal(this.currentLength);

      // If no variants with length options exist, show migration notice
      if (!price) {
        this.heroPrice.textContent = this.fmt(parseFloat(this.variants[0]?.price || '0'));
        this.heroLabel.textContent = this.t('perInch', 'per inch');
        return;
      }

      // Offcuts tab open: the list carries its own add buttons; Steps 2 and 3
      // are hidden by CSS and the price band shows the cheapest piece.
      if (this.offcutMode) { this.renderOffcutHero(); return; }
      if (this.finePrint && this.finePrint.innerHTML !== this.fineDefault) this.finePrint.innerHTML = this.fineDefault;

      // All lengths are now treated as custom-cut — check for sale + volume
      var pricingLen = Math.ceil(this.currentLength);
      // saleAll (weekly whole-SKU sale) honors compare-at on EVERY length,
      // custom cuts included; otherwise sale is limited to the listed lengths.
      var isSale = this.pricingConfig && (this.pricingConfig.saleAll || (this.pricingConfig.saleLengths || []).includes(pricingLen));
      var volTier = null;
      var customVariant = this.findVariant(pricingLen);
      if (customVariant) {
        var cwLb = this.weightToLb(customVariant.weightGrams);
        if (cwLb > 0) volTier = this.getScoreTier(parseFloat(String(customVariant.price).replace(/,/g, '')), cwLb);
      }
      const hasCustomCurve = this.getCustomCutLadder(pricingLen);
      const hasPresetLadder = this.getVolumeLadder(pricingLen);
      const isVolumeEligible = !!hasCustomCurve || !!hasPresetLadder || volTier !== null;

      // Get compare_at_price — only use for SALE lengths (not volume-only)
      // Volume-only lengths have compareAtPrice set by backend but we ignore it here;
      // volume discounts are computed client-side based on qty threshold.
      const variant = this.findVariant(price.pricingLength);
      const compareAt = variant?.compareAtPrice ? parseFloat(variant.compareAtPrice) : null;
      const fullPrice = (isSale && compareAt && compareAt > price.total) ? compareAt : price.total;

      // For volume-only lengths, the backend baked the discount into variant price.
      // We need the UN-discounted price as the base. Use compareAtPrice if available.
      const volumeBasePrice = (isVolumeEligible && !isSale && compareAt && compareAt > price.total)
        ? compareAt : price.total;

      // Effective qty = cart + PDP input. Since "Add to Cart" is a separate step,
      // show the discount preview before they commit.
      var effectiveQty = this.cartQtyForVariant + this.currentQty;

      // Volume discount on unit price — uses multi-tier ladder when available
      var finalUnit = isSale ? price.total : volumeBasePrice;
      var activeVolumeTier = null;
      var volumeLadder = null;
      if (isVolumeEligible) {
        // Try preset ladder first (exact length match), then custom cut curve
        volumeLadder = this.getVolumeLadder(pricingLen) || this.getCustomCutLadder(pricingLen);
      }
      if (isVolumeEligible && volumeLadder) {
        // Multi-tier: find the active tier for effective qty (PDP + cart)
        var tierInfo = this.getActiveTierFromLadder(volumeLadder.ladder, effectiveQty);
        if (tierInfo.active) {
          activeVolumeTier = tierInfo.active;
          // Math.round matches how Shopify computes the actual percentage discount at checkout.
          // Prior ceil() consistently overstated price by ~1c vs the Function's percentage calc.
          finalUnit = Math.round(volumeBasePrice * (1 - tierInfo.active.discount) * 100) / 100;
        } else if (!isSale && compareAt && compareAt > price.total) {
          finalUnit = compareAt;
        }
      } else if (isVolumeEligible && volTier && (!this.pricingConfig?.volumeTiers || this.isCustom) && effectiveQty >= volTier.minQty) {
        // Legacy score-based fallback: for products without pricing_config, OR for custom cuts
        // (custom cuts can use legacy scores since they can't match preset tier entries)
        activeVolumeTier = volTier;
        finalUnit = Math.round(volumeBasePrice * (1 - volTier.discount) * 100) / 100;
      } else if (isVolumeEligible && !isSale && compareAt && compareAt > price.total) {
        finalUnit = compareAt;
      }

      const totalWithQty = finalUnit * this.currentQty;
      const savingsPerUnit = fullPrice - finalUnit;
      const totalDiscountPct = fullPrice > 0 ? Math.round((1 - finalUnit / fullPrice) * 100) : 0;

      // Hero price
      this.heroPrice.textContent = this.fmt(finalUnit);
      if (this.isCustom && price.pricingLength !== this.currentLength) {
        this.heroLabel.textContent = this.t('forLengthRate', 'for {len}" ({rate}" rate)', { len: this.currentLength, rate: price.pricingLength });
      } else {
        this.heroLabel.textContent = this.t('forLength', 'for {n}"', { n: price.pricingLength });
      }

      // Hero strikethrough
      if (savingsPerUnit > 0.005) {
        this.heroOrig.textContent = this.fmt(fullPrice);
        this.heroOrig.classList.add('visible');
        var parts = [];
        if (isSale) parts.push(this.t('tagSale', 'sale'));
        if (activeVolumeTier) parts.push(this.t('tagBulk', 'bulk {pct}%', { pct: Math.round(activeVolumeTier.discount * 100) }));
        this.savingsText.textContent = this.t('youSave', 'You save {amount} ({pct}% off', { amount: this.fmt(savingsPerUnit), pct: totalDiscountPct }) + (parts.length ? ', ' + parts.join(' + ') : '') + ')';
        this.heroSavings.classList.add('visible');
      } else {
        this.heroOrig.classList.remove('visible');
        this.heroSavings.classList.remove('visible');
      }

      // Sale text below slider (step 1)
      this.renderSaleText();

      // Low stock badge next to weight (step 2)
      this.renderLowStock(variant);

      // Cut hint + stock availability check
      var exceedsStock = this.stockMaxLength > 0 && price.pricingLength > this.stockMaxLength;
      if (this.oversizeInfo) {
        // Over-max callout carries the message; keep the hint as the plain reference.
        this.cutHint.innerHTML = '*' + this.t('hintIncrements', '1/8 inch increments. Min:') + ' <span class="dp-hint-val">' + this.minLength + '"</span>, ' + this.t('hintMax', 'Max:') + ' <span class="dp-hint-val">' + this.effMaxLength() + '"</span>';
      } else if (exceedsStock) {
        this.cutHint.innerHTML = '<span class="dp-stock-warn">' + this.t('stockWarn', 'Stock not available at this length. Maximum in stock: {n}"', { n: this.stockMaxLength }) + '</span>';
      } else if (this.isCustom && price.pricingLength !== this.currentLength) {
        this.cutHint.innerHTML = this.t('pricedAtRate', '*Priced at <span class="dp-highlight">{n}"</span> rate (rounded up to the next inch). 1/8 inch increments.', { n: price.pricingLength });
      } else {
        this.cutHint.innerHTML = '*' + this.t('hintIncrements', '1/8 inch increments. Min:') + ' <span class="dp-hint-val">' + this.minLength + '"</span>, ' + this.t('hintMax', 'Max:') + ' <span class="dp-hint-val">' + this.effMaxLength() + '"</span>';
      }

      // Visual slider
      var pct = Math.min((price.pricingLength / this.maxLength) * 100, 100);
      this.sliderFill.style.width = pct + '%';

      // Step 2: Qty description
      if (this.qtyField) this.qtyField.value = this.currentQty;
      if (this.qtyDesc) {
        this.qtyDesc.innerHTML = (this.currentQty === 1
          ? this.t('qtyDescOne', '<strong>{n}</strong> x <strong>{len}"</strong> length', { n: this.currentQty, len: price.pricingLength })
          : this.t('qtyDescOther', '<strong>{n}</strong> x <strong>{len}"</strong> lengths', { n: this.currentQty, len: price.pricingLength }));
      }

      // Step 2: Shipping weight
      if (this.shipWeight) {
        var unitWeightLb = variant ? this.weightToLb(variant.weightGrams) : 0;
        var totalWeightLb = unitWeightLb * this.currentQty;
        var weightHtml = '<span class="dp-order-weight-label">' + this.t('shipWeightLabel', 'Shipping weight:') + '</span> <span class="dp-order-weight-val">' + totalWeightLb.toFixed(1) + ' lb</span>';
        if (totalWeightLb > 150) {
          weightHtml += ' <span class="dp-order-weight-warn">' + this.t('palletWarn', '(pallet shipping required above 150 lbs)') + '</span>';
        }
        this.shipWeight.innerHTML = weightHtml;
      }

      // Step 3: Summary line
      var displayPerInch = finalUnit / price.pricingLength;
      if (this.cutSummary) {
        this.cutSummary.textContent = this.t('cutSummary', '{n} x {len}" @ {price}/in', { n: this.currentQty, len: price.pricingLength, price: this.fmt(displayPerInch) });
      }

      // Volume callout (pass ladder + effective qty for progressive messaging)
      var stockLow = variant && variant.inventoryQty > 0 && variant.inventoryQty < 10;
      this.renderVolumeCallout(isVolumeEligible, volTier, activeVolumeTier, volumeBasePrice, volumeLadder, effectiveQty, this.currentLength, stockLow ? 'volume-low' : 'default');

      // Total (with orig strikethrough)
      this.cutTotal.textContent = this.fmt(totalWithQty);
      const origTotal = fullPrice * this.currentQty;
      if (origTotal - totalWithQty > 0.005 && this.totalOrig) {
        this.totalOrig.textContent = this.fmt(origTotal);
        this.totalOrig.classList.add('visible');
      } else if (this.totalOrig) {
        this.totalOrig.classList.remove('visible');
      }

      // Update hidden form fields
      this.variantIdInput.value = price.variantId;
      // Cart qty = number of cuts (variant price already includes full length cost)
      this.qtyInput.value = this.currentQty;

      // Line item property
      if (this.lineItemProps) {
        this.lineItemProps.value = this.currentLength + '"';
      }

      // Reset add to cart button state
      this.addToCartBtn.textContent = this.t('atcAdd', 'Add to Cart');
      this.addToCartBtn.classList.remove('added');
      this.addToCartBtn.disabled = false;

      // Disable add-to-cart if selected length exceeds available stock
      if (exceedsStock && this.addToCartBtn) {
        this.addToCartBtn.disabled = true;
        this.addToCartBtn.textContent = this.t('atcNotAvailable', 'Not available at this length');
      }

      // Over-max entry takes precedence: show the RFQ message + block add-to-cart
      // so the customer can't buy a length other than the one they typed.
      this.renderOversize();
      this.renderSnapNote();
      if (this.oversizeInfo && this.addToCartBtn) {
        this.addToCartBtn.disabled = true;
        this.addToCartBtn.textContent = this.t('atcEnterUpTo', 'Enter a length up to {n}"', { n: this.effMaxLength() });
      }

      // Quote capture for out-of-stock states: the disabled "Not available at
      // this length" state, and the everything-sold-out state. Gated on the
      // available flag too so untracked-inventory products never trip it.
      var noStockAtAll = this.variants.length > 0 && this.stockMaxLength === 0 &&
        !this.variants.some(function (v) { return v.available; });
      // Quantity outruns the shelf: the daily stock_state total is the pile.
      var qtyOverTotal = !exceedsStock && !noStockAtAll && this.stockTotal > 0 &&
        (this.currentLength * this.currentQty) > (this.stockTotal + 0.01);
      if (qtyOverTotal && this.addToCartBtn) {
        this.addToCartBtn.disabled = true;
        this.addToCartBtn.textContent = this.t('atcNotEnough', 'Not enough on the shelf');
      }
      this.renderStockQuote(exceedsStock, noStockAtAll, qtyOverTotal);
    }

    // Quote CTA shown when the chosen length is out of stock (ATC disabled) or
    // when nothing is in stock at all. Deep-links to the RFQ form with the
    // product, the current qty, and the customer's ORIGINAL requested length
    // (pre in-stock snap). Same rendering pattern as the over-max callout,
    // which always takes precedence.
    renderStockQuote(exceedsStock, noStockAtAll, qtyOverTotal) {
      var el = this.oosQuoteEl;
      if (!el) return;
      if (this.oversizeInfo || !(noStockAtAll || exceedsStock || qtyOverTotal)) {
        el.hidden = true;
        el.innerHTML = '';
        return;
      }
      var reqLen = (this.requestedLength != null && !isNaN(this.requestedLength))
        ? this.requestedLength : this.currentLength;
      var lenStr = this.fmtLen(reqLen);
      var longest = this.stockLongest > 0 ? this.stockLongest : this.stockMaxLength;
      var longestStr = this.fmtLen(longest);
      var totalStr = this.fmtLen(this.stockTotal);
      var tail = this.t('srcTail', 'We bring more in for orders of $250 or more, 10 to 21 days.');
      var title, body, cta, alt = '', wantStr, alertLen = 0;
      var count = this.offcuts.reduce(function (a, p) { return a + p.n; }, 0);
      if (noStockAtAll) {
        title = this.t('oosAllTitle', 'Sold out online right now');
        body = tail;
        cta = this.t('oversizeCta', 'Request a quote for {n}"', { n: lenStr });
        wantStr = lenStr + '"';
      } else if (exceedsStock) {
        // Longer than anything on the shelf today.
        title = this.t('srcLongTitle', 'Longer than anything on the shelf');
        body = this.t('srcLongBody', 'Longest piece today is {longest}". We bring more in for orders of $250 or more, 10 to 21 days.', { longest: longestStr });
        cta = this.t('oversizeCta', 'Request a quote for {n}"', { n: lenStr });
        wantStr = lenStr + '"';
        alertLen = reqLen;
        if (count > 0 && !this.offcutMode) alt = '<button type="button" class="dp-link-btn" data-dp-see-offcuts>' + this.t('offAll', 'All {n} offcuts', { n: count }) + '</button>';
      } else {
        // Quantity times length outruns the pile.
        var fits = Math.max(0, Math.floor((this.stockTotal + 0.01) / this.currentLength));
        var fitsStr = fits === 1 ? this.t('srcFitsOne', 'one') : this.t('srcFitsN', '{n}', { n: fits });
        title = this.t('srcQtyTitle', 'Not enough on the shelf for {q} × {len}"', { q: this.currentQty, len: lenStr });
        body = this.t('srcQtyBody', 'There\'s {total}" left, enough for {fits}. We bring more in for orders of $250 or more, 10 to 21 days.', { total: totalStr, fits: fitsStr });
        cta = this.t('srcCtaQty', 'Request a quote for {q} × {len}"', { q: this.currentQty, len: lenStr });
        wantStr = this.currentQty + ' × ' + lenStr + '"';
        if (fits > 0) alt = '<button type="button" class="dp-link-btn" data-dp-alt-qty="' + fits + '">' + this.t('srcOrderFits', 'Order the {fits} that\'s here', { fits: fitsStr }) + '</button>';
      }
      var note = this.t('srcNote', 'Sourcing request: {title}. Wanted {want}. Shelf today: longest {longest}", {total}" in total. 10 to 21 days, $250 minimum.', { title: this.productTitle, want: wantStr, longest: longestStr, total: totalStr });
      var href = this.buildRfqUrl(reqLen, note);
      el.innerHTML =
        '<div class="dp-oversize__title">' + title + '</div>' +
        '<div class="dp-oversize__body">' + body + '</div>' +
        '<div class="dp-oversize__acts"><a class="dp-oversize__cta" href="' + href + '">' + cta +
          '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4"/></svg></a>' + alt + '</div>' +
        this.alertHtml(alertLen);
      el.hidden = false;
    }

    // Sale text below slider (step 1) — clickable sale-length chips
    renderSaleText() {
      if (!this.saleText) return;
      // Weekly whole-SKU sale: one static line instead of per-length chips.
      // Every length carries the same percent, so chips would enumerate the
      // whole grid and the click-to-jump behavior would be meaningless.
      if (this.pricingConfig && this.pricingConfig.saleAll) {
        var wPct = Math.round((this.pricingConfig.saleDiscount || 0) * 100);
        this.saleText.innerHTML =
          '<span class="dp-sale-chip dp-sale-chip--static">' + this.t('weeklyChip', 'This week') +
          ' <span class="dp-sale-chip__pct">' + this.t('pctOff', '{n}% off', { n: wPct }) + '</span></span>' +
          '<span class="dp-weekly-note">' + this.t('weeklyEveryCut', 'Every cut of this SKU. New sale Monday.') + '</span>';
        return;
      }
      // Build sale lengths list once
      if (!this._saleLens) {
        this._saleLens = [];
        for (var i = 0; i < this.variants.length; i++) {
          var v = this.variants[i];
          var cap = v.compareAtPrice ? parseFloat(v.compareAtPrice) : 0;
          var vp = parseFloat(String(v.price).replace(/,/g, ''));
          if (cap > vp && cap > 0) {
            var len = this.optionInches(v);
            var pct = Math.round((1 - vp / cap) * 100);
            if (!isNaN(len)) this._saleLens.push({ len: len, pct: pct });
          }
        }
        this._saleLens.sort(function(a, b) { return a.len - b.len; });
        // dedupe
        var seen = {};
        this._saleLens = this._saleLens.filter(function(s) {
          if (seen[s.len]) return false;
          seen[s.len] = true;
          return true;
        });
      }
      if (this._saleLens.length > 0) {
        var self = this;
        var html = '<span class="dp-sale-label">' + this.t('deals', 'Deals:') + '</span>';
        this._saleLens.slice(0, 4).forEach(function(s) {
          html += '<button type="button" class="dp-sale-chip" data-sale-len="' + s.len + '">' + s.len + '" <span class="dp-sale-chip__pct">' + self.t('pctOff', '{n}% off', { n: s.pct }) + '</span></button>';
        });
        this.saleText.innerHTML = html;
        // Bind click handlers
        this.saleText.querySelectorAll('.dp-sale-chip').forEach(function(btn) {
          btn.addEventListener('click', function() {
            var len = parseInt(btn.dataset.saleLen, 10);
            self.currentLength = len;
            self.requestedLength = len;
            self.isCustom = true;
            var input = self.querySelector('[data-dp-custom-input]');
            if (input) input.value = len;
            self.render();
          });
        });
      } else {
        this.saleText.innerHTML = '';
      }
    }

    // Low stock badge next to weight (step 2) — pulsing dot
    renderLowStock(variant) {
      if (!this.lowStockBadge) return;
      var stock = variant ? (variant.inventoryQty || 0) : 0;
      if (stock > 0 && stock < 10) {
        this.lowStockBadge.innerHTML = '<span class="dp-low-stock-dot"></span> ' + this.t('onlyLeft', 'Only {n} left', { n: stock });
        this.lowStockBadge.classList.add('visible');
      } else {
        this.lowStockBadge.classList.remove('visible');
        this.lowStockBadge.innerHTML = '';
      }
    }

    // Snap to nearest in-stock length when current length is OOS
    snapToNearestInStock() {
      var pricingLen = Math.ceil(this.currentLength);
      var variant = this.findVariant(pricingLen);
      if (variant && (variant.inventoryQty || 0) > 0) return; // in stock, no snap needed

      // Search outward from current length for nearest in-stock variant
      var requestedLength = this.currentLength;
      for (var delta = 1; delta <= this.maxLength; delta++) {
        // Check shorter first, then longer
        var shorter = pricingLen - delta;
        if (shorter >= this.minLength) {
          var sv = this.findVariant(shorter);
          if (sv && (sv.inventoryQty || 0) > 0) {
            this._snappedFrom = requestedLength;
            this.currentLength = shorter;
            this.customInput.value = shorter;
            return;
          }
        }
        var longer = pricingLen + delta;
        if (longer <= this.maxLength) {
          var lv = this.findVariant(longer);
          if (lv && (lv.inventoryQty || 0) > 0) {
            this._snappedFrom = requestedLength;
            this.currentLength = longer;
            this.customInput.value = longer;
            return;
          }
        }
      }
      // No in-stock variant found at all — leave as-is, ATC will be disabled
    }

    renderVolumeCallout(isVolumeEligible, volTier, activeVolumeTier, unitPrice, volumeLadder, effectiveQty, currentLength, lengthType) {
      if (!this.volumeCallout) return;
      this.volumeCallout.className = 'dp-volume-callout';
      this.volumeCallout.innerHTML = '';

      // Limited stock: either ladder has lowStock flag, or length is volume-low (no tiers at all for this length)
      if (lengthType === 'volume-low' || (volumeLadder && volumeLadder.lowStock)) {
        this.volumeCallout.className = 'dp-volume-callout low-stock';
        this.volumeCallout.innerHTML = '<div class="dp-vol-main">' + this.t('volLimited', 'Volume discounts available on this product, limited stock at this length') + '</div>';
        return;
      }

      if (!isVolumeEligible || (!volTier && !volumeLadder)) return;

      var cartNote = this.cartQtyForVariant > 0 ? this.t('volInCart', ' ({n} in cart)', { n: this.cartQtyForVariant }) : '';
      // Build length description for messaging (e.g. '21.25"' or '12"')
      var lenStr = currentLength + '"';

      // Use multi-tier ladder if available
      if (volumeLadder && volumeLadder.ladder.length > 0) {
        var tierInfo = this.getActiveTierFromLadder(volumeLadder.ladder, effectiveQty);
        var ladder = volumeLadder.ladder;

        if (tierInfo.active) {
          // Discount is active — show savings + next tier teaser
          var savedPerUnit = unitPrice * tierInfo.active.discount;
          var totalSaved = savedPerUnit * effectiveQty;
          var html = '<div class="dp-vol-main">' + this.t('volSavings', 'Volume savings: {label} applied, saving {amount}', { label: tierInfo.active.label, amount: this.fmt(totalSaved) }) + cartNote + '</div>';

          if (tierInfo.next) {
            var remaining = tierInfo.next.minQty - effectiveQty;
            html += '<div class="dp-vol-next">' + (remaining === 1
              ? this.t('volNextOne', 'Add {n} more {len} length to save {label} per cut', { n: remaining, len: lenStr, label: tierInfo.next.label })
              : this.t('volNextOther', 'Add {n} more {len} lengths to save {label} per cut', { n: remaining, len: lenStr, label: tierInfo.next.label })) + '</div>';
          } else {
            html += '<div class="dp-vol-max">' + this.t('volMax', 'Maximum volume savings applied') + '</div>';
          }

          html += this.renderTierDots(ladder, effectiveQty);

          this.volumeCallout.className = 'dp-volume-callout applied';
          this.volumeCallout.innerHTML = html;
        } else {
          // Below first tier — show how many more needed
          var firstTier = ladder[0];
          var remaining = firstTier.minQty - effectiveQty;
          var html = '<div class="dp-vol-main">' + (remaining === 1
            ? this.t('volUnlockOne', 'Add {n} more {len} length to unlock {label} bulk savings', { n: remaining, len: lenStr, label: firstTier.label })
            : this.t('volUnlockOther', 'Add {n} more {len} lengths to unlock {label} bulk savings', { n: remaining, len: lenStr, label: firstTier.label })) + '</div>';
          html += this.renderTierDots(ladder, effectiveQty);
          this.volumeCallout.className = 'dp-volume-callout approaching';
          this.volumeCallout.innerHTML = html;
        }
      } else {
        // Legacy single-tier fallback
        if (activeVolumeTier) {
          var savedPerUnit = unitPrice * activeVolumeTier.discount;
          var totalSaved = savedPerUnit * effectiveQty;
          this.volumeCallout.className = 'dp-volume-callout applied';
          this.volumeCallout.innerHTML = '<div class="dp-vol-main">' + this.t('volBulk', 'Bulk discount: {pct}% applied, saving {amount}', { pct: Math.round(activeVolumeTier.discount * 100), amount: this.fmt(totalSaved) }) + cartNote + '</div>';
        } else {
          var remaining = volTier.minQty - effectiveQty;
          if (remaining > 0) {
            this.volumeCallout.className = 'dp-volume-callout approaching';
            this.volumeCallout.innerHTML = '<div class="dp-vol-main">' + (remaining === 1
              ? this.t('volSaveOne', 'Add {n} more {len} lengths to save {label} per cut', { n: remaining, len: lenStr, label: volTier.label })
              : this.t('volSaveOther', 'Add {n} more {len} lengths to save {label} per cut', { n: remaining, len: lenStr, label: volTier.label })) + '</div>';
          }
        }
      }
    }

    // Render tier progress dots for the volume callout
    renderTierDots(ladder, currentQty) {
      if (!ladder || ladder.length <= 1) return '';
      var html = '<div class="dp-tier-dots">';
      for (var i = 0; i < ladder.length; i++) {
        var tier = ladder[i];
        var reached = currentQty >= tier.minQty;
        var dotClass = 'dp-tier-dot' + (reached ? ' reached' : '');
        html += '<div class="' + dotClass + '">';
        html += '<span class="dp-tier-label">' + tier.label + '</span>';
        html += '<span class="dp-tier-qty">' + tier.minQty + '+</span>';
        html += '</div>';
        if (i < ladder.length - 1) {
          html += '<div class="dp-tier-line' + (currentQty >= ladder[i + 1].minQty ? ' reached' : '') + '"></div>';
        }
      }
      html += '</div>';
      return html;
    }

    async addToCart() {
      if (this.offcutMode) return; // the offcut rows carry their own buttons
      // Never add an over-max length — route to RFQ instead.
      if (this.oversizeInfo) { window.location.href = this.buildRfqUrl(this.oversizeInfo.reqLen); return; }
      const price = this.calcTotal(this.currentLength);
      if (!price) return;

      this.addToCartBtn.disabled = true;
      this.addToCartBtn.textContent = this.t('atcAdding', 'Adding...');

      const cartQty = this.currentQty;

      // Build sections list for cart drawer refresh (matches theme pattern from custom-cut.js)
      const cartDrawer = document.querySelector('cart-drawer');
      let sections = 'cart-icon-bubble';
      if (cartDrawer) {
        const cartSection = cartDrawer.closest('.shopify-section');
        if (cartSection) {
          sections += ',' + cartSection.id.replace('shopify-section-', '');
        }
      }

      const body = {
        items: [{
          id: price.variantId,
          quantity: cartQty,
          properties: {
            'Your Cut': this.currentLength + '"',
          }
        }],
        sections_url: window.location.pathname,
        sections: sections,
      };

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.description || this.t('atcCannotAdd', 'Could not add to cart'));
        }

        const data = await res.json();

        // Success — re-fetch cart to get accurate qty (avoids double-counting)
        this.fetchCartQty();

        this.addToCartBtn.textContent = this.t('atcAdded', 'Added! {n} x {len}" — {amount}', { n: this.currentQty, len: this.currentLength, amount: this.fmt(price.total * this.currentQty) });
        this.addToCartBtn.classList.add('added');

        // Update cart icon count first
        if (typeof ProductForm !== 'undefined' && ProductForm.updateCartIcon) {
          ProductForm.updateCartIcon(data);
        }

        // Open cart drawer or redirect to cart page (respects theme setting)
        const afterAtc = theme?.settings?.afterAtc || 'drawer';
        if (afterAtc === 'page') {
          setTimeout(() => { window.location.href = theme?.routes?.cart || '/cart'; }, 300);
          return;
        }

        if (cartDrawer && data.sections) {
          const openDrawer = afterAtc === 'drawer' || (afterAtc === 'drawer_desktop' && window.matchMedia('(min-width: 768px)').matches);
          cartDrawer.renderContents(data, openDrawer);
        } else if (!cartDrawer) {
          // Fallback: drawer element not found, redirect to cart
          setTimeout(() => { window.location.href = theme?.routes?.cart || '/cart'; }, 300);
          return;
        }

        setTimeout(() => {
          this.addToCartBtn.textContent = this.t('atcAdd', 'Add to Cart');
          this.addToCartBtn.classList.remove('added');
          this.addToCartBtn.disabled = false;
        }, 2500);

      } catch (err) {
        this.addToCartBtn.textContent = err.message || this.t('atcError', 'Error — try again');
        this.addToCartBtn.disabled = false;
        setTimeout(() => {
          this.addToCartBtn.textContent = this.t('atcAdd', 'Add to Cart');
        }, 3000);
      }
    }
  }

  // One shelf piece, one click, from its own row. `btn` is that row's button and
  // carries every state (adding, added, in your cart, error) so the customer
  // never leaves the list.
  DynamicPricing.prototype.addOffcutToCart = async function (i, btn) {
    var sel = this.offcuts[i];
    if (!sel || !btn) return;
    // After any outcome the row settles to what the cart says (syncOffcutButtons):
    // "In your cart" and greyed while the cart holds every piece of that length,
    // "Add to cart" again only once the customer removes it.
    var settle = (ms) => { setTimeout(() => { btn.dataset.busy = ''; this.fetchCartQty(); }, ms); };
    var variant = this.offcutVariant(sel.len);
    if (!variant) { btn.textContent = this.t('atcCannotAdd', 'Could not add to cart'); settle(3000); return; }
    btn.dataset.busy = '1';
    btn.disabled = true;
    btn.textContent = this.t('atcAdding', 'Adding...');
    // The cart merges a second add of the same piece into one line at
    // quantity 2 while the shelf still has one (order #4363, 2026-09-06).
    // Count what is already in the cart for this exact piece first.
    try {
      var cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      var cart = cartRes.ok ? await cartRes.json() : null;
      var already = 0;
      (cart && cart.items || []).forEach(function (it) {
        if (String(it.variant_id) === String(variant.id) && it.properties && String(it.properties._Offcut) === String(sel.len)) already += it.quantity || 0;
      });
      if ((sel.n || 1) - already <= 0) {
        btn.classList.add('is-out');
        btn.textContent = this.t('offRowInCart', 'In your cart');
        settle(0);
        return;
      }
    } catch (e) { /* cart unreadable: add one and let the Function cap it */ }
    var cartDrawer = document.querySelector('cart-drawer');
    var sections = 'cart-icon-bubble';
    if (cartDrawer) {
      var cartSection = cartDrawer.closest('.shopify-section');
      if (cartSection) sections += ',' + cartSection.id.replace('shopify-section-', '');
    }
    var body = {
      items: [{
        id: variant.id,
        quantity: 1,
        properties: {
          'Your Cut': this.fmtLen(sel.len) + '"',
          '_Offcut': String(sel.len),
        },
      }],
      sections_url: window.location.pathname,
      sections: sections,
    };
    try {
      var res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) {
        var err = await res.json();
        throw new Error(err.description || this.t('atcCannotAdd', 'Could not add to cart'));
      }
      var data = await res.json();
      this.fetchCartQty();
      btn.textContent = this.t('offRowAdded', 'Added');
      btn.classList.add('added');
      if (typeof ProductForm !== 'undefined' && ProductForm.updateCartIcon) ProductForm.updateCartIcon(data);
      var afterAtc = (typeof theme !== 'undefined' && theme && theme.settings && theme.settings.afterAtc) || 'drawer';
      if (afterAtc === 'page') { setTimeout(() => { window.location.href = (theme && theme.routes && theme.routes.cart) || '/cart'; }, 300); return; }
      if (cartDrawer && data.sections) {
        var openDrawer = afterAtc === 'drawer' || (afterAtc === 'drawer_desktop' && window.matchMedia('(min-width: 768px)').matches);
        cartDrawer.renderContents(data, openDrawer);
      } else if (!cartDrawer) {
        setTimeout(() => { window.location.href = (typeof theme !== 'undefined' && theme && theme.routes && theme.routes.cart) || '/cart'; }, 300);
        return;
      }
      settle(1500);
    } catch (e) {
      btn.textContent = e.message || this.t('atcError', 'Error — try again');
      settle(3000);
    }
  };

  customElements.define('dynamic-pricing', DynamicPricing);
}
